/**
 * CorrelationFindings - Displays metric correlation insights
 */

import React from "react";
import {Card, CardBody, Badge, Progress} from "reactstrap";
import {CorrelationFinding} from "../../types/advancedAnalysis";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";

interface CorrelationFindingsProps {
  findings: CorrelationFinding[];
}

const getRelationshipColor = (relationship: string): string => {
  switch (relationship) {
    case "inverse":
      return "danger";
    case "positive":
      return "success";
    case "threshold":
      return "warning";
    case "pattern":
      return "info";
    default:
      return "secondary";
  }
};

const getStrengthColor = (strength: number): string => {
  if (strength >= 0.8) {
    return "danger";
  }
  if (strength >= 0.6) {
    return "warning";
  }
  if (strength >= 0.4) {
    return "info";
  }
  return "secondary";
};

const getStrengthLabel = (strength: number): string => {
  if (strength >= 0.8) {
    return "Very Strong";
  }
  if (strength >= 0.6) {
    return "Strong";
  }
  if (strength >= 0.4) {
    return "Moderate";
  }
  return "Weak";
};

const CorrelationFindings: React.FC<CorrelationFindingsProps> = ({findings}) => {
  if (findings.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="fa fa-info-circle mr-2"></i>
        No correlation patterns detected.
      </div>
    );
  }

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <div>
          {findings.map((finding, index) => (
            <Card key={index} className="mb-3">
              <CardBody>
                <div className="mb-3">
                  <Badge color={getRelationshipColor(finding.Relationship)} className="mr-2">
                    {finding.Relationship}
                  </Badge>
                  <Badge color={getStrengthColor(finding.Strength)}>
                    {getStrengthLabel(finding.Strength)} ({(finding.Strength * 100).toFixed(0)}%)
                  </Badge>
                </div>

                <div className="mb-3">
                  <strong>Correlation Strength:</strong>
                  <Progress
                    value={finding.Strength * 100}
                    color={
                      color === "blue"
                        ? "primary"
                        : color === "green"
                          ? "success"
                          : color === "orange"
                            ? "warning"
                            : color === "red"
                              ? "danger"
                              : "primary"
                    }
                    className="mt-2"
                  />
                </div>

                <div className="mb-3">
                  <strong>Pattern:</strong>
                  <p className="mb-0 mt-2">{finding.Description}</p>
                </div>

                {finding.Implication && finding.Implication.trim() !== "" && (
                  <div className="alert alert-warning mb-0">
                    <strong>
                      <i className="fa fa-lightbulb mr-2"></i>
                      Implication:
                    </strong>
                    <p className="mb-0 mt-2">{finding.Implication}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default CorrelationFindings;
